import { Injectable } from '@angular/core';
import { WindowRefService, localStorageEnabled } from '@callrail/looky/util';
import { ReplaySubject } from 'rxjs';

import * as FullStory from '@fullstory/browser';

// Console pill styling for the diagnostics below. An empty style resets the
// preceding %c so a pill's background doesn't bleed into the text after it.
const PILL = 'border-radius: 3px; padding: 2px 6px; font-weight: bold;';
const FULLSTORY_TAG = `${PILL} background: #4a4a4a; color: #fff;`;
const EVENT_TAG = `${PILL} background: #f0b429; color: #3d2b00;`;
const SESSION_INIT_TAG = `${PILL} background: #2563eb; color: #fff;`;
const DISCLAIMER = 'font-weight: bold; color: #e53935;';
const RESET = '';

@Injectable({
  providedIn: 'root',
})
export class FullstoryService {
  private fsInitializedObs = new ReplaySubject<boolean>(1);
  public fsInitialized$ = this.fsInitializedObs.asObservable();

  /** Whatever `init` was given, for the session-init log's disclaimer. */
  private devMode = false;

  constructor(windowRef: WindowRefService) {
    /* eslint-disable no-underscore-dangle */
    const nativeWindow = windowRef.nativeWindow();
    const oldFn = nativeWindow._fs_ready;
    nativeWindow._fs_is_outer_script = windowRef.inIframe();
    nativeWindow._fs_ready = () => {
      if (oldFn) {
        oldFn();
      }
      this.fsInitializedObs.next(true);
    };
    /* eslint-enable no-underscore-dangle */
  }

  public get fs(): any {
    return FullStory;
  }

  public isEventLogEnabled(): boolean {
    return (
      localStorageEnabled() &&
      localStorage.getItem('fullstory_console_event_log') === 'true'
    );
  }

  public setEventLogEnabled(enabled: boolean): void {
    if (!localStorageEnabled()) {
      return;
    }
    if (enabled) {
      localStorage.setItem('fullstory_console_event_log', 'true');
    } else {
      localStorage.removeItem('fullstory_console_event_log');
    }
  }

  /**
   * Starts the FullStory session. Remembers `devMode` so the session-init log
   * below can say whether anything will actually be recorded — the option is not
   * readable back off the FullStory namespace.
   */
  public init(options: {
    orgId: string;
    devMode?: boolean;
    debug?: boolean;
  }): void {
    this.devMode = !!options.devMode;
    FullStory.init(options);
  }

  /**
   * Identifies the session's user, echoing the uid and user vars it sends when
   * the console log is on — this is the substantive payload of session startup,
   * and the vars are what FullStory segments sessions by.
   *
   * `devMode` disables recording and is the normal state outside production, so
   * the disclaimer explains why the session won't appear in FullStory rather
   * than implying something went wrong.
   */
  public identify(uid: string, userVars: object): void {
    if (this.isEventLogEnabled()) {
      this.logTagged(
        SESSION_INIT_TAG,
        'Session Init',
        this.devMode
          ? 'devMode is on — this session will not be recorded'
          : undefined,
        uid,
        userVars
      );
    }

    FullStory.identify(uid, userVars);
  }

  public addCustomEvent(eventName: string, eventParams: object): void {
    const initialized = FullStory.isInitialized();

    if (this.isEventLogEnabled()) {
      this.logTagged(
        EVENT_TAG,
        eventName,
        initialized ? undefined : 'Not sent — FullStory is not initialized',
        eventParams
      );
    }

    if (initialized) {
      FullStory.event(eventName, eventParams);
    }
  }

  /**
   * Sets scoped custom vars — currently only `'page'`, which annotates the
   * current page (and the events captured on it) rather than firing an event.
   * Guarded on initialization like {@link addCustomEvent}.
   */
  public setVars(scope: 'page', properties: object): void {
    const initialized = FullStory.isInitialized();

    if (this.isEventLogEnabled()) {
      this.logTagged(
        EVENT_TAG,
        `setVars (${scope})`,
        initialized ? undefined : 'Not sent — FullStory is not initialized',
        properties
      );
    }

    if (initialized) {
      FullStory.setVars(scope, properties);
    }
  }

  /**
   * The shared shape of both diagnostics: the dark-gray FullStory tag, a second
   * tag naming what happened, an optional red disclaimer on its own line, then
   * the payload as a live object the console can expand.
   */
  private logTagged(
    tag: string,
    label: string,
    disclaimer: string | undefined,
    ...payload: unknown[]
  ): void {
    let format = `%cFullStory%c %c${label}%c`;
    const styles = [FULLSTORY_TAG, RESET, tag, RESET];

    if (disclaimer) {
      format += `\n%c⚠️ ${disclaimer}`;
      styles.push(DISCLAIMER);
    }

    // Writing to the console is the entire point of this method. The payload
    // trails the format string so it stays a live object the console can expand,
    // and so identify's two arguments are shown as they were passed.
    // eslint-disable-next-line no-console
    console.log(format, ...styles, ...payload);
  }
}
